import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DocumentTextIcon, ArrowDownTrayIcon,
  BookmarkIcon, TrashIcon, CheckIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIconFilled } from '@heroicons/react/24/solid';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from '../../../utils/axios';
import { REACT_APP_UPLOADS } from '../../../constants';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import { splitSpecies } from '../../../utils/species';

/* ── Types ─────────────────────────────────────────────────────────── */

interface ReportStats {
  total: number;
  concluded: number;
  ongoing: number;
  absconded: number;
  convicted: number;
  convictionRate: number;
  uniqueOffenders: number;
  repeatOffenders: number;
  withCoords: number;
  topSpecies: [string, number][];
  topLoc: [string, number][];
  topNat: [string, number][];
  yearTrend: [string, number][];
}

interface Snapshot {
  id: number;
  title: string | null;
  generatedAt: string;
  speciesFilter: string;
  fromYear: string;
  toYear: string;
  namedMilestone: boolean;
  metrics: ReportStats;
}

interface Props {
  allData: any[];
  userName?: string;
}

/* ── Helpers ────────────────────────────────────────────────────────── */

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function pad(n: number) { return String(n).padStart(2, '0'); }

function formatDate(d: Date) {
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function pct(a: number, b: number) {
  return b ? `${Math.round((a / b) * 100)}%` : '0%';
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 2)   return 'just now';
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  < 7)   return `${days}d ago`;
  const d = new Date(iso);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
}

function deltaLabel(diff: number): JSX.Element | null {
  if (diff === 0) return null;
  const cls = diff > 0 ? 'text-emerald-400' : 'text-red-400';
  return <span className={`text-[9px] font-semibold ${cls}`}>{diff > 0 ? `+${diff}` : diff}</span>;
}

/* ── Main component ─────────────────────────────────────────────────── */

export default function UserReportView({ allData, userName }: Props) {
  const { state } = useContext(AuthContext);
  const token     = state.userToken ?? '';

  /* ── filter state ── */
  const [fromYear,   setFromYear]   = useState('all');
  const [toYear,     setToYear]     = useState('all');
  const [specFilter, setSpecFilter] = useState('all');
  const [generated,  setGenerated]  = useState(false);

  /* ── pdf download state ── */
  const reportRef    = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  /* ── snapshot state ── */
  const [snapshots,       setSnapshots]       = useState<Snapshot[]>([]);
  const [activeSnapshotId, setActiveSnapshotId] = useState<number | null>(null);
  const [savingSnapshot,  setSavingSnapshot]  = useState(false);
  const [labelingId,      setLabelingId]      = useState<number | null>(null);
  const [labelDraft,      setLabelDraft]      = useState('');
  const [labelSaving,     setLabelSaving]     = useState(false);

  /* Focus the label input whenever the user opens an inline editor */
  const labelInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (labelingId !== null) {
      // Small timeout lets React finish rendering the input before focusing
      const t = setTimeout(() => labelInputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [labelingId]);

  /* ── print scroll ref ── */
  const scrollRef = useRef<HTMLDivElement>(null);

  /* ── derived filter lists ── */
  const yearList = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => {
      if (d.dateOfSeizure) {
        const y = new Date(d.dateOfSeizure).getFullYear();
        if (y > 2000) set.add(String(y));
      }
    });
    return Array.from(set).sort();
  }, [allData]);

  const speciesList = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => splitSpecies(d.species).forEach((s) => set.add(s)));
    return Array.from(set).sort();
  }, [allData]);

  /* ── filtered dataset ── */
  const dataset = useMemo(() => {
    let f = allData;
    if (fromYear !== 'all') f = f.filter((d: any) =>
      d.dateOfSeizure && new Date(d.dateOfSeizure).getFullYear() >= Number(fromYear)
    );
    if (toYear !== 'all') f = f.filter((d: any) =>
      d.dateOfSeizure && new Date(d.dateOfSeizure).getFullYear() <= Number(toYear)
    );
    if (specFilter !== 'all') f = f.filter((d: any) =>
      d.species?.toLowerCase().includes(specFilter.toLowerCase())
    );
    return f;
  }, [allData, fromYear, toYear, specFilter]);

  /* ── live computed stats ── */
  const liveStats = useMemo((): ReportStats => {
    const total      = dataset.length;
    const ongoing    = dataset.filter((d: any) => d.caseStatus?.toLowerCase() === 'ongoing').length;
    const concluded  = dataset.filter((d: any) => d.caseStatus?.toLowerCase() === 'concluded').length;
    const absconded  = dataset.filter((d: any) => d.caseStatus?.toLowerCase() === 'absconded').length;
    const withCoords = dataset.filter((d: any) => d.latitude != null && d.longitude != null).length;
    const convicted  = dataset.filter((d: any) =>
      d.caseStatus?.toLowerCase() === 'concluded' && d.judgmentOutcome?.trim()
    ).length;

    const spMap: Record<string, number> = {};
    dataset.forEach((d: any) =>
      splitSpecies(d.species).forEach((s) => { spMap[s] = (spMap[s] || 0) + 1; })
    );
    const topSpecies = Object.entries(spMap).sort((a, b) => b[1] - a[1]).slice(0, 5) as [string, number][];

    const natMap: Record<string, number> = {};
    dataset.forEach((d: any) => {
      const n = d.nationality?.toUpperCase(); if (n) natMap[n] = (natMap[n] || 0) + 1;
    });
    const topNat = Object.entries(natMap).sort((a, b) => b[1] - a[1]).slice(0, 5) as [string, number][];

    const locMap: Record<string, number> = {};
    dataset.forEach((d: any) => { const l = d.location?.trim(); if (l) locMap[l] = (locMap[l] || 0) + 1; });
    const topLoc = Object.entries(locMap).sort((a, b) => b[1] - a[1]).slice(0, 5) as [string, number][];

    const yearMap: Record<string, number> = {};
    dataset.forEach((d: any) => {
      if (d.dateOfSeizure) {
        const y = new Date(d.dateOfSeizure).getFullYear();
        if (y > 2000) yearMap[y] = (yearMap[y] || 0) + 1;
      }
    });
    const yearTrend = Object.entries(yearMap).sort(([a], [b]) => Number(a) - Number(b)) as [string, number][];

    const offMap: Record<string, number> = {};
    dataset.forEach((d: any) => {
      const n = d.nameOfAccused?.trim()?.toLowerCase(); if (n) offMap[n] = (offMap[n] || 0) + 1;
    });
    const repeatOffenders = Object.values(offMap).filter((c) => c > 1).length;
    const uniqueOffenders = Object.keys(offMap).length;

    return {
      total, ongoing, concluded, absconded, convicted, withCoords,
      topSpecies, topNat, topLoc, yearTrend,
      repeatOffenders, uniqueOffenders,
      convictionRate: concluded ? Math.round((convicted / concluded) * 100) : 0,
    };
  }, [dataset]);

  /* ── active display stats: live or from selected snapshot ── */
  const activeSnapshot = activeSnapshotId !== null
    ? snapshots.find((s) => s.id === activeSnapshotId) ?? null
    : null;
  const stats: ReportStats = activeSnapshot ? activeSnapshot.metrics : liveStats;

  /* ── delta vs previous snapshot ── */
  const activeIdx = activeSnapshotId !== null
    ? snapshots.findIndex((s) => s.id === activeSnapshotId)
    : -1;
  const prevSnap: Snapshot | undefined = activeIdx >= 0
    ? snapshots[activeIdx + 1]   // next older snapshot
    : snapshots[0];               // live vs most recent
  const delta = prevSnap ? {
    total:          stats.total          - prevSnap.metrics.total,
    convictionRate: stats.convictionRate - prevSnap.metrics.convictionRate,
    concluded:      stats.concluded      - prevSnap.metrics.concluded,
    ongoing:        stats.ongoing        - prevSnap.metrics.ongoing,
    absconded:      stats.absconded      - prevSnap.metrics.absconded,
  } : null;

  /* ── load snapshots on mount ── */
  useEffect(() => {
    if (!token) return;
    axios.get(`${REACT_APP_UPLOADS}/api/v1/report-snapshots`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      setSnapshots(res.data ?? []);
      // If there are prior snapshots, mark report as having been generated before
      if ((res.data ?? []).length > 0) setGenerated(true);
    })
    .catch(() => { /* silent — user just won't see history */ });
  }, [token]);

  /* ── auto-save on generate ── */
  async function handleGenerate() {
    setGenerated(true);
    setActiveSnapshotId(null); // switch back to live view
    if (!token) return;
    setSavingSnapshot(true);
    try {
      const body = {
        speciesFilter: specFilter,
        fromYear,
        toYear,
        metrics: liveStats,
      };
      const res = await axios.post(`${REACT_APP_UPLOADS}/api/v1/report-snapshots`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnapshots((prev) => [res.data, ...prev]);
    } catch {
      /* silent — report still displays, just not persisted */
    } finally {
      setSavingSnapshot(false);
    }
  }

  /* ── PDF download: render full element to canvas → paginate into jsPDF ── */
  async function handleDownloadPDF() {
    const el = reportRef.current;
    if (!el || downloading) return;
    setDownloading(true);
    try {
      // Capture the full report element regardless of scroll position or overflow clipping
      const canvas = await html2canvas(el, {
        scale: 2,                       // 2× for sharp text on retina
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0a2e18',    // dark green background
        logging: false,
        // Expand to full scrollable height so nothing is clipped
        windowWidth:  el.scrollWidth,
        windowHeight: el.scrollHeight,
      });

      const pdf     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW   = pdf.internal.pageSize.getWidth();   // 210 mm
      const pageH   = pdf.internal.pageSize.getHeight();  // 297 mm
      const imgData = canvas.toDataURL('image/png');

      // Map the canvas (rendered at scale 2) to the PDF page width
      // canvas.width  = element.offsetWidth  * 2  pixels
      // imgWidthMM    = pageW (fills the page)
      // imgHeightMM   = proportional to canvas aspect ratio
      const imgWidthMM  = pageW;
      const imgHeightMM = (canvas.height / canvas.width) * pageW;

      let remainingMM = imgHeightMM;
      let offsetMM    = 0;

      while (remainingMM > 0) {
        // Place the image at -offsetMM so the current slice aligns to the top of the page
        pdf.addImage(imgData, 'PNG', 0, -offsetMM, imgWidthMM, imgHeightMM);
        remainingMM -= pageH;
        offsetMM    += pageH;
        if (remainingMM > 0) pdf.addPage();
      }

      const date     = new Date().toISOString().split('T')[0];
      const species  = (activeSnapshot?.speciesFilter ?? specFilter) !== 'all'
        ? `-${(activeSnapshot?.speciesFilter ?? specFilter).replace(/\s+/g, '-')}`
        : '';
      pdf.save(`WITS-Intelligence-Report${species}-${date}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloading(false);
    }
  }

  /* ── label/milestone a snapshot ── */
  const handleLabel = useCallback(async (id: number) => {
    if (!labelDraft.trim()) return;
    setLabelSaving(true);
    try {
      const res = await axios.put(
        `${REACT_APP_UPLOADS}/api/v1/report-snapshots/${id}/label`,
        { title: labelDraft.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSnapshots((prev) => prev.map((s) => s.id === id ? res.data : s));
      setLabelingId(null);
      setLabelDraft('');
    } catch {
      /* silent */
    } finally {
      setLabelSaving(false);
    }
  }, [labelDraft, token]);

  /* ── delete a snapshot ── */
  const handleDelete = useCallback(async (id: number) => {
    try {
      await axios.delete(`${REACT_APP_UPLOADS}/api/v1/report-snapshots/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnapshots((prev) => prev.filter((s) => s.id !== id));
      if (activeSnapshotId === id) setActiveSnapshotId(null);
    } catch {
      /* silent */
    }
  }, [token, activeSnapshotId]);

  /* ── misc ── */
  const reportDate = formatDate(new Date());
  const refCode    = `WITS-RPT-${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}${pad(new Date().getDate())}`;
  const selCls     = 'bg-[#0a2e18] border border-white/10 text-white/70 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/30 appearance-none';
  const showReport = generated || snapshots.length > 0;

  /* ── display period label ── */
  const periodLabel = (() => {
    const src = activeSnapshot ?? null;
    const sf = src ? src.fromYear : fromYear;
    const st = src ? src.toYear   : toYear;
    if (sf === 'all' && st === 'all') return 'All time';
    return `${sf === 'all' ? 'Start' : sf} – ${st === 'all' ? 'Present' : st}`;
  })();

  /* ═══════════════════════════════════════════════════════════════════ */

  return (
    <div
      ref={scrollRef}
      className="report-print-root flex flex-col"
      style={{ flex: 1, overflow: 'hidden', scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}
    >

      {/* ── Config bar (hidden when printing) ─────────────────────── */}
      <div
        className="print-hidden shrink-0 px-5 py-3 border-b border-white/6 flex items-center gap-3 flex-wrap z-10"
        style={{ background: '#052e16' }}
      >
        <DocumentTextIcon className="w-4 h-4 text-green-400 shrink-0" />
        <span className="text-white/55 text-xs font-semibold tracking-wider uppercase">Intelligence Report</span>
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-wrap">
          <select className={selCls} value={specFilter} onChange={(e) => setSpecFilter(e.target.value)}>
            <option value="all">All Species</option>
            {speciesList.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={selCls} value={fromYear} onChange={(e) => setFromYear(e.target.value)}>
            <option value="all">From Year</option>
            {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className={selCls} value={toYear} onChange={(e) => setToYear(e.target.value)}>
            <option value="all">To Year</option>
            {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={handleGenerate}
            disabled={savingSnapshot}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all disabled:opacity-60"
          >
            {savingSnapshot ? 'Saving…' : 'Generate Report'}
          </button>
          {showReport && (
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-white/50 text-xs hover:text-white/75 hover:bg-white/5 transition-all disabled:opacity-40"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
          )}
        </div>
      </div>

      {/* ── Body row: timeline panel + report area ─────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── Timeline sidebar (hidden when printing) ──────────────── */}
        {snapshots.length > 0 && (
          <aside
            className="print-hidden shrink-0 flex flex-col border-r border-white/6 overflow-y-auto"
            style={{ width: 220, background: '#041f0e', scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}
          >
            <div className="px-3 pt-3 pb-2">
              <p className="text-white/25 text-[9px] uppercase tracking-widest font-semibold">Report History</p>
            </div>

            {/* Live / current item */}
            <button
              onClick={() => setActiveSnapshotId(null)}
              className={`w-full text-left px-3 py-2.5 border-b border-white/5 transition-colors ${
                activeSnapshotId === null
                  ? 'bg-green-500/8 border-l-2 border-l-green-500'
                  : 'hover:bg-white/4'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                <span className="text-green-400 text-[10px] font-semibold">Live</span>
                {delta && activeSnapshotId === null && deltaLabel(delta.total)}
              </div>
              <p className="text-white/50 text-[10px]">{liveStats.total} cases</p>
              <p className="text-white/25 text-[9px]">Current data</p>
            </button>

            {/* Saved snapshots */}
            {snapshots.map((snap, idx) => {
              const isActive = activeSnapshotId === snap.id;
              const prevS    = snapshots[idx + 1];
              const snapDelta = prevS ? snap.metrics.total - prevS.metrics.total : null;
              return (
                <div
                  key={snap.id}
                  className={`border-b border-white/4 transition-colors ${
                    isActive ? 'bg-green-500/8 border-l-2 border-l-green-500/60' : 'hover:bg-white/3'
                  }`}
                >
                  {/* Label inline editor */}
                  {labelingId === snap.id ? (
                    <div className="px-3 py-2 flex flex-col gap-1.5">
                      <input
                        ref={labelInputRef}
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleLabel(snap.id);
                          if (e.key === 'Escape') { setLabelingId(null); setLabelDraft(''); }
                        }}
                        placeholder="Milestone name…"
                        className="w-full bg-white/5 border border-green-500/30 text-white/80 text-[10px] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleLabel(snap.id)}
                          disabled={labelSaving}
                          className="flex-1 flex items-center justify-center gap-1 bg-green-700/70 text-white/80 text-[9px] rounded py-1 hover:bg-green-700"
                        >
                          <CheckIcon className="w-3 h-3" /> Save
                        </button>
                        <button
                          onClick={() => { setLabelingId(null); setLabelDraft(''); }}
                          className="flex-1 flex items-center justify-center gap-1 bg-white/5 text-white/40 text-[9px] rounded py-1 hover:bg-white/10"
                        >
                          <XMarkIcon className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setActiveSnapshotId(snap.id)}
                      className="w-full text-left px-3 py-2.5"
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {snap.namedMilestone
                          ? <BookmarkSolidIconFilled className="w-2.5 h-2.5 text-amber-400 shrink-0" />
                          : <span className="w-1.5 h-1.5 rounded-full bg-white/20 shrink-0" />
                        }
                        <span className={`text-[10px] font-medium truncate ${snap.namedMilestone ? 'text-amber-300' : 'text-white/55'}`}>
                          {snap.title ?? 'Auto-saved'}
                        </span>
                        {snapDelta !== null && deltaLabel(snapDelta)}
                      </div>
                      <p className="text-white/40 text-[10px] tabular-nums">{snap.metrics.total} cases</p>
                      <p className="text-white/20 text-[9px]">{relativeTime(snap.generatedAt)}</p>
                    </button>
                  )}

                  {/* Action buttons visible on hover — always show when active */}
                  {labelingId !== snap.id && (
                    <div className={`flex gap-1 px-3 pb-2 ${isActive ? 'flex' : 'hidden group-hover:flex'}`}>
                      {!snap.namedMilestone && (
                        <button
                          title="Name as milestone"
                          onClick={() => { setLabelingId(snap.id); setLabelDraft(snap.title ?? ''); }}
                          className="flex items-center gap-0.5 text-[9px] text-white/30 hover:text-amber-400 transition-colors"
                        >
                          <BookmarkIcon className="w-3 h-3" /> Pin
                        </button>
                      )}
                      <button
                        title="Delete snapshot"
                        onClick={() => handleDelete(snap.id)}
                        className="flex items-center gap-0.5 text-[9px] text-white/20 hover:text-red-400 transition-colors ml-auto"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </aside>
        )}

        {/* ── Report content area ───────────────────────────────────── */}
        <div
          className="report-body-panel flex-1 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}
        >
          {!showReport ? (

            /* Empty state */
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-16 h-16 rounded-2xl border border-white/8 flex items-center justify-center" style={{ background: '#0a2e18' }}>
                <DocumentTextIcon className="w-8 h-8 text-white/15" />
              </div>
              <p className="text-white/40 text-sm">Configure filters above, then click <span className="text-green-400 font-semibold">Generate Report</span></p>
              <p className="text-white/20 text-xs max-w-sm text-center">Your report will update automatically as new cases are added, convictions recorded, and data enriched.</p>
            </div>

          ) : (

            /* Report document — this element is captured by html2canvas for PDF export */
            <div ref={reportRef} className="p-5 max-w-4xl mx-auto">

              {/* Snapshot context banner (shown when viewing a past snapshot) */}
              {activeSnapshot && (
                <div className="print-hidden rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 flex items-center gap-3 mb-4">
                  <BookmarkSolidIconFilled className="w-4 h-4 text-amber-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-300 text-xs font-semibold">
                      Viewing saved snapshot — {activeSnapshot.title ?? relativeTime(activeSnapshot.generatedAt)}
                    </p>
                    <p className="text-white/30 text-[10px]">
                      Generated {new Date(activeSnapshot.generatedAt).toLocaleString()} · {activeSnapshot.speciesFilter === 'all' ? 'All species' : activeSnapshot.speciesFilter} · {periodLabel}
                    </p>
                  </div>
                  {!activeSnapshot.namedMilestone && labelingId !== activeSnapshot.id && (
                    <button
                      onClick={() => { setLabelingId(activeSnapshot.id); setLabelDraft(''); }}
                      className="flex items-center gap-1 text-[10px] text-white/30 hover:text-amber-400 transition-colors border border-white/10 rounded-lg px-2.5 py-1.5"
                    >
                      <BookmarkIcon className="w-3.5 h-3.5" /> Name milestone
                    </button>
                  )}
                  {labelingId === activeSnapshot.id && (
                    <div className="flex items-center gap-1.5">
                      <input
                        value={labelDraft}
                        onChange={(e) => setLabelDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleLabel(activeSnapshot.id);
                          if (e.key === 'Escape') { setLabelingId(null); setLabelDraft(''); }
                        }}
                        placeholder="Milestone name…"
                        className="bg-white/5 border border-amber-500/30 text-white/80 text-[10px] rounded px-2 py-1 focus:outline-none w-36 placeholder-white/20"
                      />
                      <button onClick={() => handleLabel(activeSnapshot.id)} className="text-[9px] text-amber-400 border border-amber-500/30 rounded px-2 py-1">Save</button>
                      <button onClick={() => { setLabelingId(null); setLabelDraft(''); }} className="text-[9px] text-white/30 hover:text-white/50">✕</button>
                    </div>
                  )}
                </div>
              )}

              {/* Cover */}
              <div className="rounded-2xl border border-white/6 p-8 mb-5 relative overflow-hidden" style={{ background: '#0a2e18' }}>
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #4ade80, transparent)', transform: 'translate(30%, -30%)' }} />
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <p className="text-green-400 text-[10px] uppercase tracking-[0.2em] font-semibold mb-2">Wildlife Information &amp; Tracking System</p>
                    <h1 className="text-white/90 text-2xl font-bold leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
                      Wildlife Crime<br />Intelligence Report
                    </h1>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-white/20 text-[9px] uppercase tracking-widest">Ref. No.</p>
                    <p className="text-green-400/80 text-xs font-mono mt-0.5">{refCode}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-white/6">
                  {[
                    { label: 'Prepared by',   value: userName || 'WITS User' },
                    { label: 'Date issued',   value: activeSnapshot ? formatDate(new Date(activeSnapshot.generatedAt)) : reportDate },
                    { label: 'Period',        value: periodLabel },
                    { label: 'Records scope', value: `${stats.total} cases` },
                  ].map((m) => (
                    <div key={m.label}>
                      <p className="text-white/22 text-[9px] uppercase tracking-wider mb-0.5">{m.label}</p>
                      <p className="text-white/70 text-xs font-medium">{m.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Executive summary */}
              <Section title="01 — Executive Summary">
                <p className="text-white/55 text-sm leading-relaxed">
                  This report covers <strong className="text-white/80">{stats.total} wildlife crime cases</strong> recorded in the WITS database
                  {(activeSnapshot?.speciesFilter ?? specFilter) !== 'all' ? ` involving ${activeSnapshot?.speciesFilter ?? specFilter}` : ''}.
                  Of these, <strong className="text-white/80">{stats.concluded}</strong> cases ({pct(stats.concluded, stats.total)}) have been concluded,
                  with a conviction rate of <strong className="text-white/80">{stats.convictionRate}%</strong> among concluded matters.
                  {stats.ongoing > 0 && ` ${stats.ongoing} cases remain actively ongoing.`}
                  {stats.absconded > 0 && ` ${stats.absconded} accused individuals have absconded.`}
                  {` ${stats.uniqueOffenders} individuals have been accused across all cases`}
                  {stats.repeatOffenders > 0 ? `, of whom ${stats.repeatOffenders} are repeat offenders.` : '.'}
                </p>
              </Section>

              {/* Key metrics */}
              <Section title="02 — Key Metrics">
                {delta && (
                  <p className="print-hidden text-white/25 text-[10px] mb-3">
                    Changes vs previous snapshot:
                    {' '}<span className={delta.total >= 0 ? 'text-emerald-400' : 'text-red-400'}>{delta.total >= 0 ? '+' : ''}{delta.total} cases</span>
                    {delta.convictionRate !== 0 && (
                      <>{', '}<span className={delta.convictionRate >= 0 ? 'text-emerald-400' : 'text-red-400'}>{delta.convictionRate >= 0 ? '+' : ''}{delta.convictionRate}% conviction rate</span></>
                    )}
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {([
                    { label: 'Total Cases',       value: stats.total,               sub: '100% of scope',                               color: 'text-white',      d: delta?.total },
                    { label: 'Concluded',         value: stats.concluded,           sub: pct(stats.concluded, stats.total) + ' resolution rate', color: 'text-emerald-400', d: delta?.concluded },
                    { label: 'Conviction Rate',   value: `${stats.convictionRate}%`, sub: `${stats.convicted} of ${stats.concluded} concluded`, color: stats.convictionRate >= 50 ? 'text-green-400' : 'text-amber-400', d: delta?.convictionRate !== undefined ? delta.convictionRate : undefined },
                    { label: 'Ongoing Cases',     value: stats.ongoing,             sub: pct(stats.ongoing, stats.total) + ' of total',  color: 'text-violet-400', d: delta?.ongoing },
                    { label: 'Absconded',         value: stats.absconded,           sub: pct(stats.absconded, stats.total) + ' of total', color: 'text-amber-400', d: delta?.absconded },
                    { label: 'Repeat Offenders',  value: stats.repeatOffenders,     sub: `of ${stats.uniqueOffenders} total accused`,    color: 'text-red-400',    d: undefined },
                  ] as { label: string; value: number | string; sub: string; color: string; d?: number }[]).map((s) => (
                    <div key={s.label} className="rounded-xl border border-white/5 px-4 py-3" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <p className="text-white/30 text-[10px] uppercase tracking-wider">{s.label}</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <p className={`text-2xl font-bold tabular-nums ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</p>
                        {s.d !== undefined && s.d !== 0 && (
                          <span className={`print-hidden text-[10px] font-semibold ${s.d > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {s.d > 0 ? `+${s.d}` : s.d}
                          </span>
                        )}
                      </div>
                      <p className="text-white/25 text-[10px] mt-0.5">{s.sub}</p>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Species impact */}
              {stats.topSpecies.length > 0 && (
                <Section title="03 — Species Impact">
                  <p className="text-white/40 text-xs mb-3">Most frequently seized wildlife species in this reporting period.</p>
                  <div className="flex flex-col gap-2.5">
                    {stats.topSpecies.map(([name, count], i) => (
                      <div key={name} className="flex items-center gap-3">
                        <span className="text-white/20 text-[10px] tabular-nums w-4 shrink-0">{i + 1}</span>
                        <span className="text-white/65 text-xs capitalize min-w-[120px]">{name}</span>
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full bg-green-500/60 transition-all duration-700" style={{ width: pct(count, stats.topSpecies[0][1]) }} />
                        </div>
                        <span className="text-white/45 text-xs tabular-nums w-16 text-right shrink-0">{count} cases ({pct(count, stats.total)})</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Judicial outcomes */}
              <Section title="04 — Judicial Outcomes">
                <p className="text-white/40 text-xs mb-3">Distribution of case dispositions. The conviction rate is a key performance indicator for prosecutorial effectiveness.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {[
                    { label: 'Concluded with outcome', value: stats.convicted,  cls: 'text-emerald-400', bar: 'bg-emerald-500/60' },
                    { label: 'Ongoing / pending',       value: stats.ongoing,   cls: 'text-violet-400',  bar: 'bg-violet-500/60' },
                    { label: 'Accused absconded',        value: stats.absconded, cls: 'text-amber-400',   bar: 'bg-amber-500/60' },
                  ].map((s) => (
                    <div key={s.label} className="rounded-xl border border-white/5 px-4 py-3 flex flex-col gap-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
                      <p className="text-white/30 text-[10px]">{s.label}</p>
                      <p className={`text-xl font-bold tabular-nums ${s.cls}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</p>
                      <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <div className={`h-full rounded-full ${s.bar}`} style={{ width: pct(s.value, stats.total) }} />
                      </div>
                    </div>
                  ))}
                </div>
                {stats.convictionRate > 0 && (
                  <p className="text-white/35 text-xs italic">
                    Conviction rate among concluded cases stands at <span className="text-green-400 not-italic font-semibold">{stats.convictionRate}%</span>.
                    This figure grows as pending cases are adjudicated and outcomes recorded.
                  </p>
                )}
              </Section>

              {/* Geographic distribution */}
              {stats.topLoc.length > 0 && (
                <Section title="05 — Geographic Distribution">
                  <p className="text-white/40 text-xs mb-3">Hotspot locations with the highest concentration of recorded seizures.</p>
                  <div className="flex flex-col gap-2.5">
                    {stats.topLoc.map(([loc, count], i) => (
                      <div key={loc} className="flex items-center gap-3">
                        <span className="text-white/20 text-[10px] tabular-nums w-4 shrink-0">{i + 1}</span>
                        <span className="text-white/65 text-xs min-w-[150px] truncate">{loc}</span>
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full bg-sky-500/55" style={{ width: pct(count, stats.topLoc[0][1]) }} />
                        </div>
                        <span className="text-white/45 text-xs tabular-nums w-16 text-right shrink-0">{count} incidents</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Offender intelligence */}
              {stats.topNat.length > 0 && (
                <Section title="06 — Offender Intelligence">
                  <p className="text-white/40 text-xs mb-3">
                    {stats.uniqueOffenders} individuals identified across all cases.
                    {stats.repeatOffenders > 0 && ` ${stats.repeatOffenders} are known repeat offenders and warrant elevated monitoring.`}
                  </p>
                  <p className="text-white/55 text-[10px] uppercase tracking-wider mb-2">Nationalities of accused</p>
                  <div className="flex flex-col gap-2.5">
                    {stats.topNat.map(([nat, count], i) => (
                      <div key={nat} className="flex items-center gap-3">
                        <span className="text-white/20 text-[10px] tabular-nums w-4 shrink-0">{i + 1}</span>
                        <span className="text-white/65 text-xs w-20 shrink-0">{nat}</span>
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className="h-full rounded-full bg-red-500/55" style={{ width: pct(count, stats.topNat[0][1]) }} />
                        </div>
                        <span className="text-white/45 text-xs tabular-nums w-16 text-right shrink-0">{count} ({pct(count, stats.total)})</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Annual trend */}
              {stats.yearTrend.length > 1 && (
                <Section title="07 — Annual Trend">
                  <p className="text-white/40 text-xs mb-3">Seizure volume by year. Upward trends indicate increased enforcement activity or escalating wildlife crime.</p>
                  <div className="flex items-end gap-2 h-24">
                    {stats.yearTrend.map(([yr, count]) => {
                      const maxVal = Math.max(...stats.yearTrend.map(([, c]) => c));
                      const h = Math.round((count / maxVal) * 100);
                      return (
                        <div key={yr} className="flex flex-col items-center gap-1 flex-1">
                          <span className="text-white/30 text-[8px] tabular-nums">{count}</span>
                          <div className="w-full rounded-t bg-green-600/50 transition-all duration-700 min-h-[4px]" style={{ height: `${h}%` }} />
                          <span className="text-white/25 text-[8px] tabular-nums">{yr}</span>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Milestone progression (if multiple snapshots exist) */}
              {snapshots.length >= 2 && (
                <Section title="08 — Milestone Progression">
                  <p className="text-white/40 text-xs mb-4">How total case count and conviction rate have changed across your saved report snapshots.</p>
                  <div className="flex flex-col gap-2">
                    {[...snapshots].reverse().map((snap, idx, arr) => {
                      const prev = arr[idx - 1];
                      const dCases = prev ? snap.metrics.total - prev.metrics.total : null;
                      const dConv  = prev ? snap.metrics.convictionRate - prev.metrics.convictionRate : null;
                      return (
                        <div key={snap.id} className="flex items-center gap-3">
                          {/* Timeline dot */}
                          <div className="flex flex-col items-center shrink-0">
                            <div className={`w-2 h-2 rounded-full ${snap.namedMilestone ? 'bg-amber-400' : 'bg-white/20'}`} />
                            {idx < arr.length - 1 && <div className="w-px flex-1 bg-white/8 mt-0.5" style={{ height: 16 }} />}
                          </div>
                          {/* Snapshot info */}
                          <div className="flex-1 flex items-center gap-3 rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                            <div className="flex-1">
                              <p className={`text-xs font-medium ${snap.namedMilestone ? 'text-amber-300' : 'text-white/55'}`}>
                                {snap.title ?? 'Auto-saved'}
                              </p>
                              <p className="text-white/20 text-[9px]">{new Date(snap.generatedAt).toLocaleDateString()}</p>
                            </div>
                            <p className="text-white/60 text-xs tabular-nums">{snap.metrics.total} cases</p>
                            {dCases !== null && (
                              <span className={`text-[10px] tabular-nums font-semibold ${dCases > 0 ? 'text-emerald-400' : dCases < 0 ? 'text-red-400' : 'text-white/30'}`}>
                                {dCases > 0 ? `+${dCases}` : dCases}
                              </span>
                            )}
                            <span className={`text-[10px] tabular-nums ${snap.metrics.convictionRate >= 50 ? 'text-green-400' : 'text-amber-400'}`}>
                              {snap.metrics.convictionRate}% conv.
                            </span>
                            {dConv !== null && dConv !== 0 && (
                              <span className={`text-[9px] tabular-nums font-semibold ${dConv > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {dConv > 0 ? `+${dConv}` : dConv}%
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* Live current */}
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                      </div>
                      <div className="flex-1 flex items-center gap-3 rounded-lg border border-green-500/15 px-3 py-2" style={{ background: 'rgba(34,197,94,0.04)' }}>
                        <div className="flex-1">
                          <p className="text-green-400 text-xs font-medium">Live</p>
                          <p className="text-white/20 text-[9px]">Current data</p>
                        </div>
                        <p className="text-white/60 text-xs tabular-nums">{liveStats.total} cases</p>
                        {snapshots.length > 0 && (() => {
                          const d = liveStats.total - snapshots[0].metrics.total;
                          return d !== 0 ? (
                            <span className={`text-[10px] tabular-nums font-semibold ${d > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {d > 0 ? `+${d}` : d}
                            </span>
                          ) : null;
                        })()}
                        <span className={`text-[10px] tabular-nums ${liveStats.convictionRate >= 50 ? 'text-green-400' : 'text-amber-400'}`}>
                          {liveStats.convictionRate}% conv.
                        </span>
                      </div>
                    </div>
                  </div>
                </Section>
              )}

              {/* Living document notice */}
              <div className="rounded-xl border border-green-500/10 bg-green-500/4 px-5 py-4 flex items-start gap-3 mb-5">
                <ArrowDownTrayIcon className="w-4 h-4 text-green-400/60 shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-400/80 text-xs font-semibold mb-0.5">Living Document</p>
                  <p className="text-white/30 text-xs leading-relaxed">
                    This report reflects the dataset as of <strong className="text-white/50">{reportDate}</strong>. It updates automatically as cases are concluded,
                    convictions recorded, and new seizures entered. Re-generate at any time to capture the latest intelligence snapshot.
                    All past snapshots are preserved in the timeline for milestone comparison.
                  </p>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Section card ────────────────────────────────────────────────────── */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/6 p-6 mb-4" style={{ background: '#0a2e18' }}>
      <p className="text-white/55 text-[10px] font-semibold tracking-[0.15em] uppercase mb-4">{title}</p>
      {children}
    </div>
  );
}
